import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ArrowLeft, Plus, Pencil, Trash2, RotateCcw, ArrowUp, ArrowDown, Loader2, Copy, ExternalLink, Share2, Link2, Link2Off, RefreshCw, FileText, Send, CheckCircle2, XCircle, Ban, Eye, HardHat, BadgePercent, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { quotesApi, quoteLinesApi } from '../api';
import { centsToDollarString, fmtDate, fullAddress } from '../constants';
import { unitAbbr } from '../priceBookConstants';
import { QUOTE_STATUS_STYLES, quoteStatusLabel, customerActivity, fmtQty } from '../quoteConstants';
import { QuoteFormDialog } from '../components/QuoteFormDialog';
import { AiQuoteWriterDialog } from '../components/AiQuoteWriterDialog';
import { AddWorkDialog } from '../components/AddWorkDialog';
import { QuoteLineEditDialog } from '../components/QuoteLineEditDialog';
import { ConvertToProjectDialog } from '../components/ConvertToProjectDialog';
import { QuotePreviewDialog } from '../components/QuotePreviewDialog';
import { DiscountDialog } from '../components/DiscountDialog';

const err = (e, fallback) => {
  const d = e.response?.data?.detail;
  toast.error(typeof d === 'string' ? d : d?.message || fallback);
};

const ConfirmDialog = ({ open, onOpenChange, title, body, confirmLabel, confirmCls, onConfirm, busy, withReason, reasonLabel, testId }) => {
  const [reason, setReason] = useState('');
  useEffect(() => { if (open) setReason(''); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="bg-[#111111] border-gray-800 text-white max-w-md" data-testid={testId}>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <p className="text-sm text-gray-400">{body}</p>
        {withReason && (
          <div>
            <label className="text-xs text-gray-400 mb-1 block">{reasonLabel || 'Reason (optional)'}</label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="bg-[#1A1A1A] border-gray-700 text-white" data-testid={`${testId}-reason`} />
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-gray-700 text-gray-300" data-testid={`${testId}-cancel`}>Go Back</Button>
          <Button onClick={() => onConfirm(reason)} disabled={busy} className={confirmCls || 'bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold'} data-testid={`${testId}-confirm`}>
            {busy && <Loader2 size={14} className="animate-spin" />} {confirmLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function OpsQuoteBuilder() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showRemoved, setShowRemoved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [aiWriterOpen, setAiWriterOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [lineEdit, setLineEdit] = useState(null);
  const [convertOpen, setConvertOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [discountOpen, setDiscountOpen] = useState(false);
  const [confirm, setConfirm] = useState(null); // 'reopen'|'reopen-accepted'|'cancel'|'accept'|'decline'|'rotate'|'disable'|'delete'

  const refresh = useCallback(() => {
    Promise.all([quotesApi.get(quoteId), quoteLinesApi.list(quoteId, { include_removed: true })])
      .then(([q, l]) => { setQuote(q.data); setLines(l.data); })
      .catch((e) => { if (e.response?.status === 404) setNotFound(true); else toast.error('Could not load quote'); })
      .finally(() => setLoading(false));
  }, [quoteId]);

  useEffect(() => { refresh(); }, [refresh]);

  const getLinkUrl = async () => {
    const r = await quotesApi.publicLink(quoteId);
    return `${window.location.origin}/q/${r.data.token}`;
  };

  const copyLink = async () => {
    try {
      const url = await getLinkUrl();
      try { await navigator.clipboard.writeText(url); }
      catch {
        const ta = document.createElement('textarea');
        ta.value = url; document.body.appendChild(ta); ta.select();
        document.execCommand('copy'); document.body.removeChild(ta);
      }
      toast.success('Customer Quote link copied.');
    } catch { toast.error('Could not generate link'); }
  };

  const openView = async () => {
    try { window.open(await getLinkUrl(), '_blank'); } catch { toast.error('Could not open customer view'); }
  };

  const nativeShare = async () => {
    try {
      const url = await getLinkUrl();
      await navigator.share({ title: quote.title, text: 'Your BuildBroz quote is ready to review.', url });
    } catch { /* user cancelled */ }
  };

  const act = async (fn, msg, close = true) => {
    setBusy(true);
    try { await fn(); if (msg) toast.success(msg); if (close) setConfirm(null); refresh(); }
    catch (e) { err(e, 'Action failed'); }
    finally { setBusy(false); }
  };

  const publish = () => {
    if (quote.publish_blockers.length) return toast.error(quote.publish_blockers.join('; '));
    act(async () => {
      await quotesApi.publish(quoteId);
      toast.success('Quote published. Customer link is ready to share.');
    }, null);
  };

  const moveLine = (line, dir) => {
    const active = lines.filter((l) => !l.is_removed);
    const idx = active.findIndex((l) => l.id === line.id);
    const swap = idx + dir;
    if (swap < 0 || swap >= active.length) return;
    const ids = active.map((l) => l.id);
    [ids[idx], ids[swap]] = [ids[swap], ids[idx]];
    quoteLinesApi.reorder(quoteId, ids).then(refresh).catch((e) => err(e, 'Could not reorder'));
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#D4AF37]" size={28} /></div>;
  if (notFound || !quote) return (
    <Card className="bg-[#111111] border-gray-800 p-10 text-center" data-testid="quote-not-found">
      <FileText size={36} className="mx-auto text-gray-600 mb-3" />
      <p className="text-gray-400 mb-4">Quote not found.</p>
      <Button onClick={() => navigate('/ops/quotes')} variant="outline" className="border-gray-700 text-gray-300">Back to Quotes</Button>
    </Card>
  );

  const t = quote.totals;
  const activeLines = lines.filter((l) => !l.is_removed);
  const removedLines = lines.filter((l) => l.is_removed);
  const activity = customerActivity(quote);
  const isDraft = quote.status === 'draft';
  const isSent = quote.status === 'sent';
  const address = fullAddress(quote);

  const btn = 'border-gray-700 text-gray-300 hover:text-white';

  const LineRow = ({ line, showGroupHeader }) => (
    <>
      {showGroupHeader && (
        <div className="flex items-center gap-2 mt-4 mb-1" data-testid={`assembly-group-${line.assembly_instance_id}`}>
          <span className="text-[11px] font-semibold text-[#D4AF37] uppercase tracking-wider">{line.source_assembly_name_snapshot}</span>
          <div className="flex-1 border-t border-[#D4AF37]/20" />
        </div>
      )}
      <Card className={`bg-[#141414] border-gray-800 p-3 ${line.is_removed ? 'opacity-50' : ''}`} data-testid={`quote-line-${line.id}`}>
        <div className="flex justify-between items-start gap-3">
          <div className="min-w-0">
            <div className="text-white text-sm font-medium">{line.name}
              {line.section_name && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-gray-800 text-gray-400">{line.section_name}</span>}
              {line.price_source === 'custom' && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-purple-900/30 text-purple-400 border border-purple-800">Custom Price</span>}
            </div>
            {line.description && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{line.description}</p>}
            <div className="text-xs text-gray-400 mt-1">
              {fmtQty(line.quantity_decimal)} {unitAbbr(line)} × {line.customer_unit_price_cents == null ? <span className="text-amber-400">No price</span> : centsToDollarString(line.customer_unit_price_cents)}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-white font-semibold text-sm">{line.customer_line_total_cents == null ? <span className="text-amber-400 text-xs">Incomplete</span> : centsToDollarString(line.customer_line_total_cents)}</div>
            {isDraft && !line.is_removed && (
              <div className="flex gap-1 mt-2 justify-end">
                <button onClick={() => moveLine(line, -1)} className="p-1 text-gray-500 hover:text-white" data-testid={`line-up-${line.id}`}><ArrowUp size={13} /></button>
                <button onClick={() => moveLine(line, 1)} className="p-1 text-gray-500 hover:text-white" data-testid={`line-down-${line.id}`}><ArrowDown size={13} /></button>
                <button onClick={() => setLineEdit(line)} className="p-1 text-gray-500 hover:text-[#D4AF37]" data-testid={`line-edit-${line.id}`}><Pencil size={13} /></button>
                <button onClick={() => act(() => quoteLinesApi.remove(quoteId, line.id), 'Line removed', false)} className="p-1 text-gray-500 hover:text-red-400" data-testid={`line-remove-${line.id}`}><Trash2 size={13} /></button>
              </div>
            )}
            {isDraft && line.is_removed && (
              <Button size="sm" variant="outline" onClick={() => act(() => quoteLinesApi.restore(quoteId, line.id), 'Line restored', false)} className={`${btn} mt-2 h-7 text-xs`} data-testid={`line-restore-${line.id}`}>
                <RotateCcw size={12} /> Restore
              </Button>
            )}
          </div>
        </div>
      </Card>
    </>
  );

  return (
    <div data-testid="quote-builder">
      <Link to="/ops/quotes" className="inline-flex items-center gap-1 text-sm text-gray-400 hover:text-[#D4AF37] mb-4" data-testid="back-to-quotes"><ArrowLeft size={14} /> Quotes</Link>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl md:text-2xl font-bold text-white" data-testid="quote-title">{quote.title}</h2>
            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${QUOTE_STATUS_STYLES[quote.status]}`} data-testid="quote-status-badge">{quoteStatusLabel(quote.status)}</span>
            {quote.is_expired && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border bg-amber-900/30 text-amber-400 border-amber-800" data-testid="quote-expired-badge">Expired</span>}
            {quote.converted_project && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40" data-testid="quote-converted-badge">Converted</span>}
            {quote.is_archived && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border bg-gray-800 text-gray-400 border-gray-700">Archived</span>}
          </div>
          <p className="text-sm text-gray-400 mt-1" data-testid="quote-meta">
            <span className="text-[#D4AF37]">{quote.quote_number}</span> · {quote.customer_name}
            {address && <> · {address}</>}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">Quote date {fmtDate(quote.quote_date)}{quote.valid_until && <> · Valid until {fmtDate(quote.valid_until)}</>}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isDraft && <>
            <Button variant="outline" onClick={() => setEditOpen(true)} className={btn} data-testid="edit-quote-btn"><Pencil size={14} /> Edit Quote</Button>
            <Button variant="outline" onClick={() => setAiWriterOpen(true)} className="border-[#D4AF37]/60 text-[#D4AF37] hover:text-[#B8941F] hover:border-[#D4AF37]" data-testid="ai-writer-btn"><Sparkles size={14} /> AI Writer</Button>
            <Button variant="outline" onClick={() => setPreviewOpen(true)} className={btn} data-testid="preview-quote-btn"><Eye size={14} /> Preview</Button>
            <Button onClick={() => setAddOpen(true)} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="add-work-btn"><Plus size={14} /> Add Work</Button>
            <Button onClick={publish} className="bg-blue-600 hover:bg-blue-500 text-white font-semibold" data-testid="publish-btn"><Send size={14} /> Publish &amp; Get Customer Link</Button>
          </>}
          {isSent && <>
            <Button variant="outline" onClick={copyLink} className={btn} data-testid="copy-link-btn"><Copy size={14} /> Copy Customer Link</Button>
            <Button variant="outline" onClick={openView} className={btn} data-testid="open-customer-view-btn"><ExternalLink size={14} /> Open Customer View</Button>
            {typeof navigator !== 'undefined' && navigator.share && <Button variant="outline" onClick={nativeShare} className={btn} data-testid="share-btn"><Share2 size={14} /> Share</Button>}
            <Button variant="outline" onClick={() => setConfirm('accept')} className="border-green-800 text-green-400 hover:text-green-300" data-testid="mark-accepted-btn"><CheckCircle2 size={14} /> Mark Accepted</Button>
            <Button variant="outline" onClick={() => setConfirm('decline')} className="border-red-800 text-red-400 hover:text-red-300" data-testid="mark-declined-btn"><XCircle size={14} /> Mark Declined</Button>
            <Button variant="outline" onClick={() => setConfirm('reopen')} className={btn} data-testid="reopen-btn"><RotateCcw size={14} /> Reopen</Button>
          </>}
          {quote.status === 'accepted' && <>
            {quote.converted_project ? (
              <Button onClick={() => navigate(`/ops/projects/${quote.converted_project.id}?tab=quote-change-orders`)} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="open-project-btn">
                <HardHat size={14} /> Open Project
              </Button>
            ) : (
              <Button onClick={() => setConvertOpen(true)} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="convert-to-project-btn">
                <HardHat size={14} /> Convert to Project
              </Button>
            )}
            <Button variant="outline" onClick={copyLink} className={btn} data-testid="copy-link-btn"><Copy size={14} /> Copy Customer Link</Button>
            <Button variant="outline" onClick={openView} className={btn} data-testid="open-customer-view-btn"><ExternalLink size={14} /> Open Customer View</Button>
            {!quote.converted_project && (
              <Button variant="outline" onClick={() => setConfirm('reopen-accepted')} className={btn} data-testid="reopen-edit-btn"><Pencil size={14} /> Reopen &amp; Edit</Button>
            )}
          </>}
          {(quote.status === 'declined' || quote.status === 'cancelled') && <>
            <Button variant="outline" onClick={openView} className={btn} data-testid="open-customer-view-btn"><Eye size={14} /> Open Customer View</Button>
            <Button variant="outline" onClick={() => setConfirm('reopen')} className={btn} data-testid="reopen-btn"><RotateCcw size={14} /> Reopen</Button>
          </>}
          {(isDraft || isSent) && <Button variant="outline" onClick={() => setConfirm('cancel')} className="border-orange-900 text-orange-400 hover:text-orange-300" data-testid="cancel-quote-btn"><Ban size={14} /> Cancel</Button>}
          <Button variant="outline" onClick={() => setConfirm('delete')} className="border-red-900 text-red-400 hover:text-red-300" data-testid="delete-quote-btn"><Trash2 size={14} /> Delete</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lines */}
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-semibold">Quote Lines <span className="text-gray-500 font-normal text-sm">({t.active_line_count} active)</span></h3>
            {removedLines.length > 0 && (
              <button onClick={() => setShowRemoved((v) => !v)} className="text-xs text-gray-400 hover:text-white" data-testid="toggle-removed-lines">
                {showRemoved ? 'Hide' : 'Show'} removed ({removedLines.length})
              </button>
            )}
          </div>
          {activeLines.length === 0 ? (
            <Card className="bg-[#111111] border-gray-800 p-8 text-center" data-testid="lines-empty-state">
              <p className="text-gray-400">No work on this quote yet.</p>
              {isDraft && <Button onClick={() => setAddOpen(true)} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold mt-4" data-testid="empty-add-work-btn"><Plus size={14} /> Add Work</Button>}
            </Card>
          ) : (
            <div className="space-y-2">
              {activeLines.map((line, i) => (
                <LineRow key={line.id} line={line}
                  showGroupHeader={!!line.assembly_instance_id && (i === 0 || activeLines[i - 1].assembly_instance_id !== line.assembly_instance_id)} />
              ))}
            </div>
          )}
          {showRemoved && removedLines.length > 0 && (
            <div className="mt-5">
              <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-2">Removed Lines</h4>
              <div className="space-y-2">{removedLines.map((line) => <LineRow key={line.id} line={line} showGroupHeader={false} />)}</div>
            </div>
          )}
        </div>

        {/* Summary sidebar */}
        <div className="space-y-4">
          <Card className="bg-[#111111] border-gray-800 p-4" data-testid="quote-totals-panel">
            <h3 className="text-white font-semibold mb-3">Totals</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-400">Customer Subtotal</span>
                <span className="text-white font-semibold" data-testid="totals-customer-subtotal">{t.customer_subtotal_cents == null ? (t.active_line_count ? <span className="text-amber-400 text-xs">Incomplete</span> : '—') : centsToDollarString(t.customer_subtotal_cents)}</span></div>
              {t.discount_percent != null && (
                <div className="flex justify-between"><span className="text-gray-400">Discount ({t.discount_percent}%)</span>
                  <span className="text-[#D4AF37]" data-testid="totals-discount">−{centsToDollarString(t.discount_cents)}</span></div>
              )}
              <div className="flex justify-between border-t border-gray-800 pt-2">
                <span className="text-gray-300 font-semibold">Quote Total</span>
                <span className="text-[#D4AF37] font-bold" data-testid="totals-quote-total">{t.quote_total_cents == null ? (t.active_line_count ? <span className="text-amber-400 text-xs">Incomplete</span> : '—') : centsToDollarString(t.quote_total_cents)}</span>
              </div>
            </div>
            {!quote.converted_project && quote.status !== 'cancelled' && (
              <Button size="sm" variant="outline" onClick={() => setDiscountOpen(true)}
                className="border-[#D4AF37]/50 text-[#D4AF37] hover:text-[#B8941F] w-full mt-3 text-xs" data-testid="apply-discount-btn">
                <BadgePercent size={13} /> {t.discount_percent != null ? 'Edit Discount' : 'Apply Discount'}
              </Button>
            )}
            {quote.warnings.length > 0 && (
              <div className="mt-3 space-y-1" data-testid="totals-warnings">
                {quote.warnings.map((w) => (
                  <p key={w.code} className="text-xs text-amber-400 bg-amber-900/20 border border-amber-800 rounded px-2 py-1.5">
                    {w.count} line{w.count > 1 ? 's are' : ' is'} missing Customer Pricing.
                  </p>
                ))}
              </div>
            )}
            {isDraft && quote.publish_blockers.length > 0 && (
              <div className="mt-3 text-xs text-gray-500" data-testid="publish-blockers">
                <span className="text-gray-400">Before publishing:</span>
                <ul className="list-disc ml-4 mt-1 space-y-0.5">{quote.publish_blockers.map((b, i) => <li key={i}>{b}</li>)}</ul>
              </div>
            )}
          </Card>

          {isSent && (
            <Card className="bg-[#111111] border-gray-800 p-4" data-testid="share-panel">
              <h3 className="text-white font-semibold mb-2">Customer Link</h3>
              <p className="text-xs text-gray-500 mb-3">{quote.public_link_enabled ? 'The customer link is active. Copy it and send it by text or email.' : 'The customer link is disabled. The customer page shows a generic unavailable message.'}</p>
              <div className="flex flex-wrap gap-2">
                {quote.public_link_enabled ? (
                  <Button size="sm" variant="outline" onClick={() => setConfirm('disable')} className={`${btn} text-xs`} data-testid="disable-link-btn"><Link2Off size={13} /> Disable Link</Button>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => act(() => quotesApi.enableLink(quoteId), 'Customer link enabled', false)} className={`${btn} text-xs`} data-testid="enable-link-btn"><Link2 size={13} /> Enable Link</Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setConfirm('rotate')} className={`${btn} text-xs`} data-testid="rotate-link-btn"><RefreshCw size={13} /> Rotate Link</Button>
              </div>
            </Card>
          )}

          {quote.converted_project && (
            <Card className="bg-[#111111] border-[#D4AF37]/30 p-4" data-testid="converted-project-panel">
              <h3 className="text-white font-semibold mb-1">Converted to Project</h3>
              <p className="text-sm text-gray-300">{quote.converted_project.name}</p>
              <p className="text-xs text-gray-500 mt-0.5 capitalize">Status: {quote.converted_project.status.replace(/_/g, ' ')}{quote.converted_project.is_archived ? ' · Archived' : ''}</p>
              <Button size="sm" variant="outline" onClick={() => navigate(`/ops/projects/${quote.converted_project.id}?tab=quote-change-orders`)} className="border-gray-700 text-gray-300 hover:text-white mt-3 text-xs" data-testid="converted-open-project-btn">
                <HardHat size={13} /> Open Project
              </Button>
            </Card>
          )}

          <Card className="bg-[#111111] border-gray-800 p-4" data-testid="customer-activity-panel">
            <h3 className="text-white font-semibold mb-2">Customer Activity</h3>
            <p className={`text-sm ${activity.cls}`} data-testid="customer-activity-label">{activity.label}</p>
            {quote.customer_view_count > 0 ? (
              <div className="text-xs text-gray-500 mt-2 space-y-0.5" data-testid="customer-view-info">
                <p>First viewed: {new Date(quote.first_customer_viewed_at).toLocaleString()}</p>
                <p>Last viewed: {new Date(quote.last_customer_viewed_at).toLocaleString()}</p>
                <p>View count: {quote.customer_view_count}</p>
              </div>
            ) : quote.status !== 'draft' && <p className="text-xs text-gray-500 mt-2">Not viewed yet</p>}
            {quote.status === 'accepted' && (
              <div className="text-xs text-green-400 mt-3 bg-green-900/20 border border-green-900 rounded p-2" data-testid="customer-response-info">
                {quote.accepted_via === 'customer_link'
                  ? <>Approved online by <b>{quote.accepted_by_customer_name}</b>{quote.accepted_by_customer_email && <> ({quote.accepted_by_customer_email})</>} on {fmtDate(quote.accepted_at?.slice(0, 10))}</>
                  : <>Marked accepted internally by {quote.accepted_by_username} on {fmtDate(quote.accepted_at?.slice(0, 10))}</>}
              </div>
            )}
            {quote.status === 'declined' && (
              <div className="text-xs text-red-400 mt-3 bg-red-900/20 border border-red-900 rounded p-2" data-testid="customer-response-info">
                {quote.declined_via === 'customer_link'
                  ? <>Declined online by <b>{quote.declined_by_customer_name}</b> on {fmtDate(quote.declined_at?.slice(0, 10))}</>
                  : <>Marked declined internally by {quote.declined_by_username} on {fmtDate(quote.declined_at?.slice(0, 10))}</>}
                {quote.decline_reason && <p className="mt-1 text-gray-400">Reason: {quote.decline_reason}</p>}
              </div>
            )}
          </Card>

          {quote.internal_notes && (
            <Card className="bg-[#111111] border-gray-800 p-4">
              <h3 className="text-white font-semibold mb-1 text-sm">Internal Notes</h3>
              <p className="text-xs text-gray-400 whitespace-pre-wrap">{quote.internal_notes}</p>
            </Card>
          )}
        </div>
      </div>

      {/* Dialogs */}
      <QuoteFormDialog open={editOpen} onOpenChange={setEditOpen} quote={quote} onSaved={refresh} />
      <AiQuoteWriterDialog open={aiWriterOpen} onOpenChange={setAiWriterOpen} quote={quote} onSaved={refresh} />
      <AddWorkDialog open={addOpen} onOpenChange={setAddOpen} quote={quote} onAdded={refresh} />
      <QuoteLineEditDialog open={!!lineEdit} onOpenChange={(v) => !v && setLineEdit(null)} quote={quote} line={lineEdit} onSaved={refresh} />
      <ConvertToProjectDialog open={convertOpen} onOpenChange={setConvertOpen} quote={quote}
        onConverted={(project) => navigate(`/ops/projects/${project.id}?tab=quote-change-orders`)} />
      <QuotePreviewDialog open={previewOpen} onOpenChange={setPreviewOpen} quoteId={quoteId} />
      <DiscountDialog open={discountOpen} onOpenChange={setDiscountOpen} quote={quote} onSaved={refresh} />
      <ConfirmDialog open={confirm === 'reopen-accepted'} onOpenChange={(v) => !v && setConfirm(null)} testId="reopen-accepted-dialog"
        title="Reopen this accepted quote for editing?" confirmLabel={'Reopen & Edit'} busy={busy}
        body="The customer link that was sent will be permanently disabled and the acceptance will be archived. The quote returns to Draft so you can make changes — publish again to get a fresh link to send to the customer."
        onConfirm={() => act(() => quotesApi.reopen(quoteId), 'Quote reopened as Draft. Old customer link is now disabled.')} />
      <ConfirmDialog open={confirm === 'reopen'} onOpenChange={(v) => !v && setConfirm(null)} testId="reopen-dialog"
        title="Reopen this quote?" confirmLabel="Reopen to Draft" busy={busy}
        body="Reopening this Quote will temporarily remove the customer-facing Quote from view until it is published again."
        onConfirm={() => act(() => quotesApi.reopen(quoteId), 'Quote reopened as Draft')} />
      <ConfirmDialog open={confirm === 'cancel'} onOpenChange={(v) => !v && setConfirm(null)} testId="cancel-dialog"
        title="Cancel this quote?" confirmLabel="Cancel Quote" busy={busy} withReason reasonLabel="Cancel reason (optional)"
        confirmCls="bg-orange-700 hover:bg-orange-600 text-white font-semibold"
        body="The customer page will show this quote is no longer active."
        onConfirm={(reason) => act(() => quotesApi.cancel(quoteId, reason), 'Quote cancelled')} />
      <ConfirmDialog open={confirm === 'accept'} onOpenChange={(v) => !v && setConfirm(null)} testId="internal-accept-dialog"
        title="Mark Accepted Internally?" confirmLabel="Mark Accepted" busy={busy}
        confirmCls="bg-green-700 hover:bg-green-600 text-white font-semibold"
        body="This records an internal acceptance (not a customer online approval). The quote becomes immutable."
        onConfirm={() => act(() => quotesApi.accept(quoteId), 'Quote marked accepted')} />
      <ConfirmDialog open={confirm === 'decline'} onOpenChange={(v) => !v && setConfirm(null)} testId="internal-decline-dialog"
        title="Mark Declined Internally?" confirmLabel="Mark Declined" busy={busy} withReason reasonLabel="Decline reason (optional)"
        confirmCls="bg-red-700 hover:bg-red-600 text-white font-semibold"
        body="This records an internal decline (not a customer online response)."
        onConfirm={(reason) => act(() => quotesApi.decline(quoteId, reason), 'Quote marked declined')} />
      <ConfirmDialog open={confirm === 'rotate'} onOpenChange={(v) => !v && setConfirm(null)} testId="rotate-dialog"
        title="Rotate the customer link?" confirmLabel="Rotate Link" busy={busy}
        body="The current customer URL will stop working immediately. A new link will be generated — copy and re-send it to the customer."
        onConfirm={() => act(() => quotesApi.rotateLink(quoteId), 'Customer link rotated. Old links no longer work.')} />
      <ConfirmDialog open={confirm === 'disable'} onOpenChange={(v) => !v && setConfirm(null)} testId="disable-dialog"
        title="Disable the customer link?" confirmLabel="Disable Link" busy={busy}
        body='The customer page will show "This quote is not currently available." until the link is re-enabled.'
        onConfirm={() => act(() => quotesApi.disableLink(quoteId), 'Customer link disabled')} />
      <ConfirmDialog open={confirm === 'delete'} onOpenChange={(v) => !v && setConfirm(null)} testId="delete-quote-dialog"
        title="Delete this quote permanently?" confirmLabel="Delete Quote" busy={busy}
        confirmCls="bg-red-700 hover:bg-red-600 text-white font-semibold"
        body={`"${quote.title}" (${quote.quote_number}) and all of its line items will be permanently deleted.${quote.sent_at ? " This quote was shared with the customer — their link will stop working and show 'this quote no longer exists'." : ''} This cannot be undone.`}
        onConfirm={async () => {
          setBusy(true);
          try {
            await quotesApi.delete(quoteId);
            toast.success('Quote deleted');
            navigate('/ops/quotes');
          } catch (e) { err(e, 'Could not delete quote'); setBusy(false); }
        }} />
    </div>
  );
}
